/**
* Content lastmod registry — real per-path timestamps for sitemap
* `<lastmod>` freshness signals. Update these when you meaningfully
* refresh a category, cluster of pages, or static route so Google
* schedules re-crawls promptly.
*
* Resolution order in `getLastmod(path)`:
* 1. Exact path match in EXACT_LASTMOD
* 2. Longest matching prefix in PREFIX_LASTMOD
* 3. DEFAULT_LASTMOD fallback
*/

// Build-frozen fallback: refreshed with every deploy that touches this file.
// Keep as ISO string; bump when you publish a sitewide content wave.
export const DEFAULT_LASTMOD = "2026-07-18T00:00:00.000Z";

/**
* Exact-path timestamps. Use for singular routes (home, /about, etc.)
* that were rewritten on a specific date.
*/
export const EXACT_LASTMOD: Record<string, string> = {
  "/accounting": "2026-10-08T02:51:00.000Z",
  "/accounting/questions": "2026-10-08T03:02:00.000Z",
  "/corporate": "2026-10-08T02:28:00.000Z",
  "/estate": "2026-10-08T01:37:00.000Z",
  "/litigation": "2026-10-08T01:05:00.000Z",
  "/pdpa": "2026-10-08T00:45:00.000Z",
  "/family": "2026-10-08T00:29:39.000Z",
  "/due-diligence": "2026-10-08T00:29:39.000Z",
  "/visa": "2026-10-08T00:21:02.000Z",
  "/en/visa": "2026-10-08T00:21:02.000Z",
  "/zh/visa": "2026-10-08T00:21:02.000Z",
  "/ja/visa": "2026-10-08T00:21:02.000Z",
  "/ko/visa": "2026-10-08T00:21:02.000Z",
  "/vi/visa": "2026-10-08T00:21:02.000Z",
  "/id/visa": "2026-10-08T00:21:02.000Z",

  "/": "2026-07-16T00:00:00.000Z",
  "/services": "2026-07-16T00:00:00.000Z",
  "/pricing": "2026-07-14T00:00:00.000Z",
  "/about": "2026-07-10T00:00:00.000Z",
  "/coverage": "2026-07-14T00:00:00.000Z",
  "/blog": "2026-07-16T00:00:00.000Z",
  "/faq": "2026-07-12T00:00:00.000Z",
  "/contact": "2026-07-10T00:00:00.000Z",
  "/authors": "2026-07-10T00:00:00.000Z",
  "/tools/fee-estimator": "2026-07-12T00:00:00.000Z",
  "/editorial-standards": "2026-07-10T00:00:00.000Z",
  // Wave 10 Phase 4 — HowTo schema wired into locale services routes
  "/en/services": "2026-07-16T12:00:00.000Z",
  "/zh/services": "2026-07-16T12:00:00.000Z",
  "/ja/services": "2026-07-16T12:00:00.000Z",
  "/ko/services": "2026-07-16T12:00:00.000Z",
  "/vi/services": "2026-07-16T12:00:00.000Z",
  "/id/services": "2026-07-16T12:00:00.000Z",
  // Wave 10 Phase 9 — mobile header polish + locale about/contact/coverage refresh
  "/en/about": "2026-07-16T22:00:00.000Z",
  "/zh/about": "2026-07-16T22:00:00.000Z",
  "/ja/about": "2026-07-16T22:00:00.000Z",
  "/ko/about": "2026-07-16T22:00:00.000Z",
  "/vi/about": "2026-07-16T22:00:00.000Z",
  "/id/about": "2026-07-16T22:00:00.000Z",
  "/en/contact": "2026-07-16T22:00:00.000Z",
  "/zh/contact": "2026-07-16T22:00:00.000Z",
  "/ja/contact": "2026-07-16T22:00:00.000Z",
  "/ko/contact": "2026-07-16T22:00:00.000Z",
  "/vi/contact": "2026-07-16T22:00:00.000Z",
  "/id/contact": "2026-07-16T22:00:00.000Z",
  "/en/coverage": "2026-07-16T22:00:00.000Z",
  "/zh/coverage": "2026-07-16T22:00:00.000Z",
  "/ja/coverage": "2026-07-16T22:00:00.000Z",
  "/ko/coverage": "2026-07-16T22:00:00.000Z",
  "/vi/coverage": "2026-07-16T22:00:00.000Z",
  "/id/coverage": "2026-07-16T22:00:00.000Z",
  // Wave 12 — cluster directory hubs published (orphan-page fix)
  "/lgz/spoke": "2026-07-30T00:00:00.000Z",
  "/lgz/intent": "2026-07-30T00:00:00.000Z",
  "/pcc/spoke": "2026-07-30T00:00:00.000Z",
  "/pcc/intent": "2026-07-30T00:00:00.000Z",
  "/pcc/documents": "2026-07-30T00:00:00.000Z",
  "/pcc/embassy": "2026-07-30T00:00:00.000Z",
  "/litigation/spoke": "2026-07-30T00:00:00.000Z",
  "/corporate/spoke": "2026-07-30T00:00:00.000Z",
  "/ip/spoke": "2026-07-30T00:00:00.000Z",
  "/longstay/matrix": "2026-07-30T00:00:00.000Z",
  "/marriage/matrix": "2026-07-30T00:00:00.000Z",
  "/visa/matrix": "2026-07-30T00:00:00.000Z",
  "/visa/intent": "2026-07-30T00:00:00.000Z",
  "/moj/intent": "2026-07-30T00:00:00.000Z",
  "/naati/intent": "2026-07-30T00:00:00.000Z",
  "/naati/spoke": "2026-07-30T00:00:00.000Z",
  "/sworn/intent": "2026-07-30T00:00:00.000Z",
  "/translation/intent": "2026-07-30T00:00:00.000Z",
  "/apostille/intent": "2026-07-30T00:00:00.000Z",

};

/**
* Prefix-based timestamps. Longest match wins. Every path under the
* prefix inherits the timestamp unless overridden by EXACT_LASTMOD.
*
* Wave 10 Phase 6 rewrote service categories with AeoTrustBlock,
* so `/services/` gets a very recent timestamp.
*/
export const PREFIX_LASTMOD: Array<[string, string]> = [
  ["/accounting/services/", "2026-10-08T02:51:00.000Z"],
  ["/accounting/for/", "2026-10-08T02:51:00.000Z"],
  ["/corporate/services/", "2026-10-08T02:28:00.000Z"],
  ["/estate/services/", "2026-10-08T01:37:00.000Z"],
  ["/litigation/services/", "2026-10-08T01:05:00.000Z"],
  ["/pdpa/services/", "2026-10-08T00:45:00.000Z"],
  ["/family/topic/", "2026-10-08T00:29:39.000Z"],
  ["/due-diligence/topic/", "2026-10-08T00:29:39.000Z"],
  ["/visa/services/", "2026-10-08T00:21:02.000Z"],
  ["/visa/country/", "2026-10-08T00:21:02.000Z"],
  ["/visa/matrix/", "2026-10-08T00:21:02.000Z"],

  // L7 — accounting, tax, payroll and Indian PAN source-led refresh
  ["/accounting/", "2026-08-04T00:00:00.000Z"],
  ["/accounting", "2026-08-04T00:00:00.000Z"],
  // Wave 11.6 — per-locale /naati/services/$service mirrors (240 URLs)
  ["/naati/", "2026-07-17T18:00:00.000Z"],
  ["/naati", "2026-07-17T18:00:00.000Z"],
  ["/en/naati", "2026-07-17T18:00:00.000Z"],
  ["/zh/naati", "2026-07-17T18:00:00.000Z"],
  ["/ja/naati", "2026-07-17T18:00:00.000Z"],
  ["/ko/naati", "2026-07-17T18:00:00.000Z"],
  ["/vi/naati", "2026-07-17T18:00:00.000Z"],
  ["/id/naati", "2026-07-17T18:00:00.000Z"],
  // Wave 10 Phase 6 — AEO trust block + citation cluster
  ["/services/", "2026-07-16T00:00:00.000Z"],
  // Wave 8 — coverage rank push refresh
  ["/coverage/bangkok/", "2026-07-14T00:00:00.000Z"],
  ["/coverage/", "2026-07-14T00:00:00.000Z"],
  // Legal pages — refreshed on template updates only
  ["/legal/", "2026-06-01T00:00:00.000Z"],
  // Author bios refresh cadence
  ["/authors/", "2026-07-10T00:00:00.000Z"],
];

/** Resolve the best lastmod ISO string for the given path. */
export function getLastmod(path: string): string {
  const exact = EXACT_LASTMOD[path];
  if (exact) return exact;
  let best: { prefix: string; ts: string } | null = null;
  for (const [prefix, ts] of PREFIX_LASTMOD) {
    if (path.startsWith(prefix) && (!best || prefix.length > best.prefix.length)) {
      best = { prefix, ts };
    }
  }
  return best?.ts ?? DEFAULT_LASTMOD;
}
